import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState, PasswordInput } from '../../components/ui.js';
import { relativeTime, absoluteTime } from '../../lib/format.js';
import type { SsoConnection, SsoDefaultRole, SsoProtocol } from '../../lib/types.js';

const PROVIDER_LABEL: Record<SsoConnection['providerHint'], string> = {
  google: 'Google Workspace',
  microsoft: 'Microsoft Entra ID',
  okta: 'Okta',
  generic: 'OpenID Connect',
};

const PROTOCOL_LABEL: Record<SsoProtocol, string> = {
  oidc: 'OIDC',
  saml: 'SAML 2.0',
};

export function SsoScreen(): ReactNode {
  const connections = useQuery(() => api.ssoConnections(), []);
  const toast = useToast();
  const [editing, setEditing] = useState<SsoConnection | 'new' | null>(null);
  const [scimToken, setScimToken] = useState<{ connection: SsoConnection; token: string } | null>(null);

  async function rotateScim(c: SsoConnection): Promise<void> {
    if (c.hasScimToken && !confirm(`Replace the existing SCIM token for “${c.name}”? The old one stops working immediately.`)) {
      return;
    }
    try {
      const { token } = await api.rotateScimToken(c.id);
      setScimToken({ connection: c, token });
      connections.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function remove(c: SsoConnection): Promise<void> {
    if (!confirm(`Delete the “${c.name}” connection? Users linked to it keep their account but can no longer sign in through it.`))
      return;
    try {
      await api.deleteSsoConnection(c.id);
      toast.push('Connection deleted', 'success');
      connections.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function test(c: SsoConnection): Promise<void> {
    try {
      const res = await api.testSsoConnection(c.id);
      const okMessage = c.protocol === 'saml' ? 'SAML settings are valid' : 'Issuer is reachable';
      toast.push(res.ok ? okMessage : `Failed: ${res.error ?? 'unknown error'}`, res.ok ? 'success' : 'error');
    } catch (err) {
      toast.error(err);
    }
  }

  async function toggleEnabled(c: SsoConnection): Promise<void> {
    try {
      await api.updateSsoConnection(c.id, {
        name: c.name,
        protocol: c.protocol,
        issuer: c.issuer,
        clientId: c.clientId,
        samlIdpEntityId: c.samlIdpEntityId,
        samlIdpSsoUrl: c.samlIdpSsoUrl,
        samlIdpCertificate: c.samlIdpCertificate,
        samlNameIdFormat: c.samlNameIdFormat,
        samlAllowIdpInitiated: c.samlAllowIdpInitiated,
        samlSignRequests: c.samlSignRequests,
        defaultRole: c.defaultRole,
        allowJit: c.allowJit,
        enforced: c.enforced,
        domainHint: c.domainHint,
        enabled: !c.enabled,
        scimEnabled: c.scimEnabled,
      });
      connections.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Page
      title="Single sign-on"
      actions={
        <button className="btn primary" onClick={() => setEditing('new')}>
          Add connection
        </button>
      }
    >
      <p className="muted small" style={{ marginBottom: 12 }}>
        Let staff sign in with an existing OIDC or SAML 2.0 identity provider (Google Workspace,
        Microsoft Entra ID, Okta, Auth0, Keycloak, ADFS, …) instead of — or in addition to — a
        console password.
      </p>

      {connections.loading && !connections.data ? <Loading what="SSO connections" /> : null}
      {connections.error ? <ErrorNote error={connections.error} /> : null}

      {connections.data ? (
        connections.data.length === 0 ? (
          <EmptyState>No SSO connections yet.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Issuer / IdP</th>
                  <th>Default role</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {connections.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {c.name}
                      <div className="faint">
                        {PROTOCOL_LABEL[c.protocol]}
                        {c.protocol === 'oidc' ? ` · ${PROVIDER_LABEL[c.providerHint]}` : null}
                      </div>
                    </td>
                    <td className="mono small" style={{ wordBreak: 'break-all' }}>
                      {c.protocol === 'saml' ? c.samlIdpEntityId : c.issuer}
                    </td>
                    <td className="small">{c.defaultRole}</td>
                    <td className="small">
                      {c.enabled ? <span className="badge good">enabled</span> : <span className="badge bad">disabled</span>}
                      {c.enforced ? <span className="faint"> · enforced</span> : null}
                      {!c.allowJit ? <span className="faint"> · no auto-provisioning</span> : null}
                      {c.lastUsedAt ? (
                        <div className="faint" title={absoluteTime(c.lastUsedAt)}>
                          last used {relativeTime(c.lastUsedAt)}
                        </div>
                      ) : (
                        <div className="faint">never used</div>
                      )}
                      {c.scimEnabled ? (
                        <div className="faint">SCIM {c.hasScimToken ? 'enabled' : 'enabled, no token yet'}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn small" onClick={() => void test(c)}>
                          Test
                        </button>
                        <button className="btn small" onClick={() => void toggleEnabled(c)}>
                          {c.enabled ? 'Disable' : 'Enable'}
                        </button>
                        {c.scimEnabled ? (
                          <button className="btn small" onClick={() => void rotateScim(c)}>
                            {c.hasScimToken ? 'Rotate SCIM token' : 'Generate SCIM token'}
                          </button>
                        ) : null}
                        <button className="btn small" onClick={() => setEditing(c)}>
                          Edit
                        </button>
                        <button className="btn small danger" onClick={() => void remove(c)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {editing ? (
        <ConnectionModal
          connection={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            connections.refetch();
          }}
        />
      ) : null}

      {scimToken ? (
        <Modal title="SCIM token" onClose={() => setScimToken(null)}>
          <p className="muted small" style={{ marginTop: 0 }}>
            Paste this token and the base URL into “{scimToken.connection.name}”'s SCIM app setup in
            your IdP. Shown once — it can't be retrieved again, only rotated.
          </p>
          <label className="field">
            Base URL
            <input readOnly value={scimToken.connection.scimBaseUrl} onFocus={(e) => e.currentTarget.select()} />
          </label>
          <label className="field">
            Bearer token
            <input
              readOnly
              className="mono"
              value={scimToken.token}
              onFocus={(e) => e.currentTarget.select()}
            />
          </label>
          <div className="row between" style={{ marginTop: 10 }}>
            <button
              className="btn small"
              onClick={() => {
                void navigator.clipboard?.writeText(scimToken.token).then(() => toast.push('Copied'));
              }}
            >
              Copy token
            </button>
            <button className="btn primary small" onClick={() => setScimToken(null)}>
              Done
            </button>
          </div>
        </Modal>
      ) : null}
    </Page>
  );
}

function ConnectionModal({
  connection,
  onClose,
  onSaved,
}: {
  connection: SsoConnection | null;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const toast = useToast();
  const [protocol, setProtocol] = useState<SsoProtocol>(connection?.protocol ?? 'oidc');
  const [name, setName] = useState(connection?.name ?? '');
  const [issuer, setIssuer] = useState(connection?.issuer ?? '');
  const [clientId, setClientId] = useState(connection?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [samlIdpEntityId, setSamlIdpEntityId] = useState(connection?.samlIdpEntityId ?? '');
  const [samlIdpSsoUrl, setSamlIdpSsoUrl] = useState(connection?.samlIdpSsoUrl ?? '');
  const [samlIdpCertificate, setSamlIdpCertificate] = useState(connection?.samlIdpCertificate ?? '');
  const [samlNameIdFormat, setSamlNameIdFormat] = useState(connection?.samlNameIdFormat ?? '');
  const [samlAllowIdpInitiated, setSamlAllowIdpInitiated] = useState(connection?.samlAllowIdpInitiated ?? false);
  const [samlSignRequests, setSamlSignRequests] = useState(connection?.samlSignRequests ?? false);
  const [samlSpCertificate, setSamlSpCertificate] = useState(connection?.samlSpCertificate ?? null);
  const [metadataSource, setMetadataSource] = useState('');
  const [importing, setImporting] = useState(false);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [defaultRole, setDefaultRole] = useState<SsoDefaultRole>(connection?.defaultRole ?? 'viewer');
  const [allowJit, setAllowJit] = useState(connection?.allowJit ?? true);
  const [enforced, setEnforced] = useState(connection?.enforced ?? false);
  const [domainHint, setDomainHint] = useState(connection?.domainHint ?? '');
  const [scimEnabled, setScimEnabled] = useState(connection?.scimEnabled ?? false);
  const [busy, setBusy] = useState(false);

  const isOidc = protocol === 'oidc';
  const isSaml = protocol === 'saml';

  // One box for both: pasted XML starts with `<`, anything else is a URL.
  async function importMetadata(): Promise<void> {
    const source = metadataSource.trim();
    setImporting(true);
    try {
      const imported = await api.importSamlMetadata(source.startsWith('<') ? { xml: source } : { url: source });
      setSamlIdpEntityId(imported.entityId);
      setSamlIdpSsoUrl(imported.ssoUrl);
      setSamlIdpCertificate(imported.certificate);
      if (imported.nameIdFormat) setSamlNameIdFormat(imported.nameIdFormat);
      setImportWarnings(imported.warnings);
      setMetadataSource('');
      toast.push('Metadata imported — review the fields below, then save', 'success');
    } catch (err) {
      toast.error(err);
    } finally {
      setImporting(false);
    }
  }

  async function rotateSpKey(): Promise<void> {
    if (!connection) return;
    if (
      !confirm(
        "Generate a new SP signing key? While request signing is on, sign-in fails until the IdP re-imports this connection's SP metadata.",
      )
    ) {
      return;
    }
    try {
      const updated = await api.rotateSamlSpKey(connection.id);
      setSamlSpCertificate(updated.samlSpCertificate);
      toast.push('New SP signing key generated', 'success');
    } catch (err) {
      toast.error(err);
    }
  }

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        protocol,
        issuer: issuer.trim(),
        clientId: clientId.trim(),
        samlIdpEntityId: samlIdpEntityId.trim(),
        samlIdpSsoUrl: samlIdpSsoUrl.trim(),
        samlIdpCertificate: samlIdpCertificate.trim(),
        samlNameIdFormat: samlNameIdFormat.trim() || null,
        samlAllowIdpInitiated,
        samlSignRequests,
        defaultRole,
        allowJit,
        enforced,
        domainHint: domainHint.trim() || null,
        scimEnabled,
      };
      if (clientSecret.trim()) body.clientSecret = clientSecret.trim();

      if (connection) {
        await api.updateSsoConnection(connection.id, body);
      } else {
        await api.createSsoConnection(body);
      }
      onSaved();
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title={connection ? 'Edit SSO connection' : 'Add SSO connection'} onClose={onClose} wide>
      <div className="stack">
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Google Workspace" autoFocus />
        </label>
        <label className="field">
          Protocol
          <select
            value={protocol}
            disabled={connection !== null}
            onChange={(e) => setProtocol(e.target.value as SsoProtocol)}
          >
            <option value="oidc">OIDC</option>
            <option value="saml">SAML 2.0</option>
          </select>
          {connection ? <span className="muted small">The protocol can't be changed after a connection is created.</span> : null}
        </label>

        {isOidc ? (
          <>
            <label className="field">
              Issuer
              <input
                value={issuer}
                onChange={(e) => setIssuer(e.target.value)}
                placeholder="https://accounts.google.com"
              />
              <span className="muted small">
                The base URL of the IdP — its <code>/.well-known/openid-configuration</code> is fetched from here.
              </span>
            </label>
            <label className="field">
              Client ID
              <input value={clientId} onChange={(e) => setClientId(e.target.value)} />
            </label>
            <label className="field">
              Client secret {connection ? <span className="muted small">(leave blank to keep the current one)</span> : null}
              <PasswordInput
                value={clientSecret}
                onChange={setClientSecret}
                placeholder={connection?.hasSecret ? '••••••••' : ''}
              />
            </label>
          </>
        ) : null}

        {isSaml ? (
          <>
            <div className="field">
              Import from IdP metadata (optional)
              <div className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
                <textarea
                  className="mono small"
                  rows={2}
                  style={{ flex: 1 }}
                  value={metadataSource}
                  onChange={(e) => setMetadataSource(e.target.value)}
                  placeholder="https://idp.example.com/metadata — or paste the metadata XML"
                />
                <button
                  className="btn small"
                  disabled={importing || metadataSource.trim() === ''}
                  onClick={() => void importMetadata()}
                >
                  {importing ? 'Importing…' : 'Import'}
                </button>
              </div>
              <span className="muted small">
                Fills in the entity ID, SSO URL and certificate below. Nothing is saved until you save.
              </span>
              {importWarnings.map((w) => (
                <span key={w} className="small">
                  ⚠ {w}
                </span>
              ))}
            </div>
            <label className="field">
              IdP entity ID
              <input
                value={samlIdpEntityId}
                onChange={(e) => setSamlIdpEntityId(e.target.value)}
                placeholder="https://idp.example.com/metadata"
              />
            </label>
            <label className="field">
              IdP SSO URL
              <input
                value={samlIdpSsoUrl}
                onChange={(e) => setSamlIdpSsoUrl(e.target.value)}
                placeholder="https://idp.example.com/sso"
              />
              <span className="muted small">Where the browser is redirected to sign in (HTTP-Redirect binding).</span>
            </label>
            <label className="field">
              IdP certificate (PEM)
              <textarea
                className="mono small"
                rows={6}
                value={samlIdpCertificate}
                onChange={(e) => setSamlIdpCertificate(e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              />
              <span className="muted small">The IdP's public signing certificate — used to verify every assertion.</span>
            </label>
            {connection?.samlSpMetadataUrl ? (
              <label className="field">
                This connection's SP metadata
                <input readOnly value={connection.samlSpMetadataUrl} onFocus={(e) => e.currentTarget.select()} />
                <span className="muted small">Give this URL to the IdP admin to auto-configure their side.</span>
              </label>
            ) : null}
            {connection?.samlSpAcsUrl ? (
              <label className="field">
                ACS URL
                <input readOnly value={connection.samlSpAcsUrl} onFocus={(e) => e.currentTarget.select()} />
                <span className="muted small">
                  Where the IdP posts its response — some IdPs ask for this instead of the metadata URL.
                </span>
              </label>
            ) : null}
            <label className="field">
              Name ID format (optional)
              <input
                className="mono small"
                value={samlNameIdFormat}
                onChange={(e) => setSamlNameIdFormat(e.target.value)}
                placeholder="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress"
              />
            </label>
            <label className="row" style={{ gap: 6 }}>
              <input type="checkbox" checked={samlSignRequests} onChange={(e) => setSamlSignRequests(e.target.checked)} />
              <span className="small">
                Sign authentication requests — for IdPs that require it (e.g. ADFS). A signing key is generated on
                save.
              </span>
            </label>
            {samlSpCertificate ? (
              <div className="field">
                SP signing certificate
                <textarea className="mono small" rows={4} readOnly value={samlSpCertificate} />
                <div className="row between">
                  <span className="muted small">
                    Also published in the SP metadata — the IdP needs it to verify signed requests.
                  </span>
                  <button className="btn small" onClick={() => void rotateSpKey()}>
                    Rotate key
                  </button>
                </div>
              </div>
            ) : null}
            <label className="row" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={samlAllowIdpInitiated}
                onChange={(e) => setSamlAllowIdpInitiated(e.target.checked)}
              />
              <span className="small">
                Allow IdP-initiated sign-in — lets users start from the IdP's app dashboard. Each assertion is
                accepted only once, but it isn't tied to a request from this console, so only enable this if your
                users need it.
              </span>
            </label>
          </>
        ) : null}

        <label className="field">
          Default role for new accounts
          <select value={defaultRole} onChange={(e) => setDefaultRole(e.target.value as SsoDefaultRole)}>
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Administrator</option>
          </select>
        </label>
        <label className="field">
          Domain hint (optional)
          <input
            value={domainHint}
            onChange={(e) => setDomainHint(e.target.value)}
            placeholder="akarya.id"
          />
          <span className="muted small">Cosmetic only — for your own reference.</span>
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={allowJit} onChange={(e) => setAllowJit(e.target.checked)} />
          <span className="small">Automatically create an account on first sign-in</span>
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={enforced} onChange={(e) => setEnforced(e.target.checked)} />
          <span className="small">
            Require SSO — disables password sign-in for everyone in this organisation except Owner
          </span>
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={scimEnabled} onChange={(e) => setScimEnabled(e.target.checked)} />
          <span className="small">
            Enable SCIM — lets this IdP push user provisioning and deprovisioning, independent of login
          </span>
        </label>
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={
              busy ||
              name.trim() === '' ||
              (isOidc && (issuer.trim() === '' || clientId.trim() === '' || (!connection && !clientSecret.trim()))) ||
              (isSaml && (samlIdpEntityId.trim() === '' || samlIdpSsoUrl.trim() === '' || samlIdpCertificate.trim() === ''))
            }
            onClick={() => void submit()}
          >
            {busy ? 'Saving…' : connection ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
